import { parseISO } from 'date-fns';
import _startCase from 'lodash.startcase';

import { MESSAGE_TYPE } from '@constants/messages';
import { formatIsoDistanceToNow } from '@helpers/date';
import { vfileRoutes, vsuiteRoutes } from '@routes';

/**
 * @typedef MessageParticipant User detail
 * @prop {string} id User ID
 * @prop {string} firstName User first name
 * @prop {string} lastName User last name
 * @prop {string} email User email address
 */

/**
 * @typedef MessageApiData
 * @prop {string} id Message ID
 * @prop {MessageParticipant} userFrom Sender details
 * @prop {string} messageThread Thread ID
 * @prop {string} messageType Message type
 * @prop {string} savedFilingPeriod Related saved filing period ID
 * @prop {string} subject Message subject
 * @prop {string} body Message content
 * @prop {Array<MessageParticipant>} participants List of cc'ed users
 * @prop {Object} messageStatus Message status info
 * @prop {string} messageStatus.id Status ID
 * @prop {boolean} messageStatus.read Has message been read
 * @prop {boolean} messageStatus.archived Is message archived
 * @prop {string} attachedFileName Name of attached file
 * @prop {string} attachmentId ID of attached file
 * @prop {string} entityCountryVatReturnSearch Name of related return
 * @prop {string} dateCreated Message creation date in ISO format
 * @prop {string} dateModified Message last modified date in ISO format
 */

/**
 * @typedef Message
 * @prop {string} id Message ID
 * @prop {string} body Message content
 * @prop {string} attachmentId ID of attached file
 * @prop {string} attachedFileName Name of attached file
 * @prop {Object} dateModified Modified date info
 * @prop {string} dateModified.label Formatted display label for date
 * @prop {Date} dateModified.date Last modified date
 * @prop {MessageParticipant} userFrom Sender details
 */

/**
 * @typedef MessagesModel
 * @prop {string} threadId Message thread ID
 * @prop {string} messageType Message type
 * @prop {Object} messageTypeLink Message type link
 * @prop {string} messageTypeLink.label Link label
 * @prop {string} messageTypeLink.to Url
 * @prop {string} filingLink Link to related filing
 * @prop {string} savedFilingPeriod Related saved filing period ID
 * @prop {string} subject Message subject
 * @prop {MessageParticipant} userFrom Sender details
 * @prop {Array<MessageParticipant>} participants List of all thread participants
 * @prop {string} statusId Message status ID
 * @prop {boolean} isRead Has message been read
 * @prop {boolean} isArchived Is message archived
 * @prop {Array<Message>} messages List of messages in thread
 */

/**
 * Transforms API data for messages into correct structure
 * @param {Array<MessageApiData>} [messages=[]] Message data from API
 * @returns {MessageModel} Transformed message data
 */
export const messageDetailGetModel = (messages = []) => {
  const latestMessage = messages?.[0] || {};
  const { messageType, savedFilingPeriod, entityCountryVatReturnSearch } = latestMessage;

  return {
    threadId: latestMessage.messageThread,
    messageType,
    messageTypeLink: messageType
      ? {
          to:
            messageType === MESSAGE_TYPE.GENERAL
              ? vsuiteRoutes.MESSAGES.make({ category: messageType })
              : vsuiteRoutes.MESSAGES.make({ savedFilingPeriod }),
          label: entityCountryVatReturnSearch || _startCase(messageType),
        }
      : undefined,
    filingLink: savedFilingPeriod ? vfileRoutes.FILING.make(savedFilingPeriod) : undefined,
    savedFilingPeriod,
    subject: latestMessage.subject,
    userFrom: latestMessage.userFrom,
    participants: latestMessage.participants || [],
    statusId: latestMessage.messageStatus?.id,
    isRead: !!latestMessage.messageStatus?.read,
    isArchived: !!latestMessage.messageStatus?.archived,
    entityCountryVatReturnSearch,
    messages: messages
      .sort((a, b) => (a.dateModified > b.dateModified ? 1 : -1))
      .map(message => ({
        id: message.id,
        body: message.body,
        attachedFileName: message.attachedFileName,
        attachmentId: message.attachmentId,
        dateModified: {
          label: formatIsoDistanceToNow(message.dateModified),
          date: parseISO(message.dateModified),
        },
        userFrom: message.userFrom,
      })),
  };
};
